// v2 ostrich — the full strider: long bare two-segment legs (defined thigh and
// shank) on big two-toe feet, feathered thigh fluff, a deep plume body with
// drooping white wing plumes and the fanned white tail, a very long bare
// S-curved neck and a small flat head with a wide beak and large eyes. Two
// plumages: the black-and-white male and the dusty grey-brown female. Ported 1:1
// from age-of-mcp's v2/ostrich.ts, using the library's shared `safari`
// (eyes, tube) helpers.
import * as THREE from "three";
import { defineModel, safari, type Part } from "../../lib";

const PLUMES = [
  { plume: "#26262b", fluff: "#33333a", accent: "#f2f2ec", skin: "#d9a98e",
    legSkin: "#c9a284", beak: "#c98a4e" },
  { plume: "#6b5f52", fluff: "#7a6e60", accent: "#cfc4b2", skin: "#cfa78c",
    legSkin: "#bf9d80", beak: "#bf8348" },
];

function ostrichParts(v: number): Part[] {
  const c = PLUMES[v % PLUMES.length]!;
  const parts: Part[] = [];
  // long bare legs: thigh, shank and a big two-toe foot, per side
  for (const s of [1, -1] as const) {
    parts.push(
      safari.tube([0, 0.235, s * 0.025], [0.004, 0.115, s * 0.026],
        0.011, 0.008, c.legSkin, 5),
      safari.tube([0.004, 0.115, s * 0.026], [0, 0.012, s * 0.026],
        0.008, 0.0065, c.legSkin, 5),
      { geometry: new THREE.BoxGeometry(0.034, 0.012, 0.013),
        color: c.legSkin, position: [0.012, 0.006, s * 0.026] },
      { geometry: new THREE.IcosahedronGeometry(0.021, 0), color: c.fluff,
        position: [0, 0.225, s * 0.03], scale: [1.1, 0.95, 0.7] },
    );
  }
  parts.push(
    // deep plume body with a soft back layer
    { geometry: new THREE.IcosahedronGeometry(0.068, 0), color: c.plume,
      position: [-0.005, 0.275, 0], scale: [1.3, 1.05, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.052, 0), color: c.fluff,
      position: [-0.04, 0.305, 0], scale: [1.05, 0.9, 1.02] },
    // drooping white wing plumes and the fanned white tail
    { geometry: new THREE.IcosahedronGeometry(0.038, 0), color: c.accent,
      position: [0, 0.265, 0.058], scale: [1.25, 0.85, 0.55] },
    { geometry: new THREE.IcosahedronGeometry(0.038, 0), color: c.accent,
      position: [0, 0.265, -0.058], scale: [1.25, 0.85, 0.55] },
    { geometry: new THREE.IcosahedronGeometry(0.045, 0), color: c.accent,
      position: [-0.082, 0.29, 0], scale: [0.8, 1.05, 1.1] },
    // the long bare S-curved neck
    safari.tube([0.045, 0.325, 0], [0.085, 0.43, 0], 0.0135, 0.0105, c.skin, 5),
    safari.tube([0.085, 0.43, 0], [0.107, 0.505, 0], 0.0105, 0.0085, c.skin, 5),
    // small flat head, wide beak, large eyes
    { geometry: new THREE.IcosahedronGeometry(0.0205, 0), color: c.skin,
      position: [0.114, 0.518, 0], scale: [1.3, 0.95, 0.95] },
    { geometry: new THREE.ConeGeometry(0.009, 0.027, 4), color: c.beak,
      position: [0.146, 0.514, 0], rotation: [0, 0, -Math.PI / 2] },
  );
  parts.push(...safari.eyes(0.122, 0.527, 0.0145, 0.0055, "#1c1812"));
  return parts;
}

export const ostrichV2 = defineModel({
  id: "ostrich-v2",
  batchId: "fable",
  name: "Ostrich (v2)",
  tags: ["creature","ostrich","low-poly","age-of-mcp","fable"],
  summary: "Low-poly ostrich.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.13,
  fits: ["draft"],
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => ostrichParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => ostrichParts(1) },
  ],
  animations: [],
});
